import { useQuery } from '@tanstack/react-query';
import type {
  DashboardSummary,
  InboundOverview,
  InventoryOverview,
  OutboundOverview,
} from '@zenarty/shared';
import {
  AlarmClock,
  AlertTriangle,
  CalendarCheck,
  Forklift,
  Send,
  Timer,
  Truck,
  Boxes,
  Building2,
  ClipboardCheck,
  PackageCheck,
  CheckCircle2,
  Circle,
  History,
  MapPin,
  MonitorSmartphone,
  Package,
  ShieldAlert,
  ShieldCheck,
  Store,
  Tag,
  Users,
} from 'lucide-react';
import { useNavigate } from 'react-router';
import { api } from '../api';
import { useAuth } from '../auth';
import { AuditTimeline } from '../components/audit';
import { BarChart, ShareBars } from '../components/charts';
import { Badge, Button, Card, ErrorMessage, PageHeader, Skeleton, Stat } from '../components/kit';
import { formatNumber, pct } from '../lib/format';

function greeting() {
  const h = new Date().getHours();
  return h < 12 ? 'Good morning' : h < 18 ? 'Good afternoon' : 'Good evening';
}

export function DashboardPage() {
  const { me, can } = useAuth();
  const navigate = useNavigate();
  const q = useQuery({
    queryKey: ['dashboard'],
    queryFn: () => api<DashboardSummary>('/dashboard/summary'),
    refetchInterval: 60_000,
  });
  const d = q.data;
  const inv = useQuery({
    queryKey: ['inventory', 'overview', ''],
    queryFn: () => api<InventoryOverview>('/inventory/overview'),
    enabled: can('inventory:read'),
    refetchInterval: 60_000,
  });
  const o = inv.data;
  const inb = useQuery({
    queryKey: ['receipts', 'overview', ''],
    queryFn: () => api<InboundOverview>('/inbound/overview'),
    enabled: can('inbound:read'),
    refetchInterval: 60_000,
  });
  const out = useQuery({
    queryKey: ['orders', 'overview', ''],
    queryFn: () => api<OutboundOverview>('/outbound/overview'),
    enabled: can('outbound:read'),
    refetchInterval: 60_000,
  });
  const ib = inb.data;
  const ob = out.data;
  const first = me?.name.split(' ')[0] ?? '';

  return (
    <div className="stack-lg">
      <PageHeader
        title={`${greeting()}, ${first}`}
        description={`${me?.operatorName ?? ''} — network overview. Figures cover the sites and clients you can access and refresh every minute.`}
        actions={
          <Button icon={History} onClick={() => void navigate('/audit')} disabled={!d?.recent}>
            Activity log
          </Button>
        }
      />
      <ErrorMessage error={q.error} />

      {ib || ob ? (
        <div className="grid-stats">
          {ob ? (
            <Stat
              label="Orders due today"
              icon={CalendarCheck}
              value={formatNumber(ob.dueToday)}
              foot={`${formatNumber(ob.shippedToday)} shipped · ${formatNumber(ob.unitsShippedToday)} units`}
              onClick={() => void navigate('/outbound/orders')}
            />
          ) : null}
          {ob ? (
            <Stat
              label="At risk / late"
              icon={AlarmClock}
              tone={ob.late ? 'danger' : ob.atRisk ? 'warn' : 'ok'}
              value={`${ob.atRisk} / ${ob.late}`}
              foot={`${ob.onHold} on hold · ${ob.backordered} backordered`}
              onClick={() =>
                void navigate(can('outbound:manage') ? '/outbound/board' : '/outbound/orders')
              }
            />
          ) : null}
          {ob ? (
            <Stat
              label="Fill rate (30 d)"
              icon={Send}
              tone="ok"
              value={ob.fillRate30d === null ? '—' : `${ob.fillRate30d}%`}
              foot={
                ob.onTime30d === null
                  ? 'No shipments yet'
                  : `${ob.onTime30d}% shipped before cut-off`
              }
            />
          ) : null}
          {ib ? (
            <Stat
              label="Trailers at the dock"
              icon={Truck}
              tone={ib.atDock + ib.receiving ? 'warn' : 'accent'}
              value={formatNumber(ib.atDock + ib.receiving)}
              foot={`${ib.expectedToday} receipts expected today`}
              onClick={() => void navigate('/inbound/receipts')}
            />
          ) : null}
          {ib ? (
            <Stat
              label="Dock-to-stock (7 d)"
              icon={ib.openPutaway ? Forklift : Timer}
              value={
                ib.avgDockToStockMinutes === null
                  ? '—'
                  : ib.avgDockToStockMinutes < 60
                    ? `${ib.avgDockToStockMinutes} min`
                    : `${(ib.avgDockToStockMinutes / 60).toFixed(1)} h`
              }
              foot={`${ib.openPutaway} LPNs waiting for putaway`}
              onClick={() => void navigate('/inbound/putaway')}
            />
          ) : null}
        </div>
      ) : null}

      <div className="grid-stats">
        {d?.sites !== null && (
          <Stat
            label="Sites"
            icon={Building2}
            value={d ? formatNumber(d.sites?.active) : <Skeleton width={60} height={28} />}
            foot={
              d?.sites
                ? `${d.sites.total - d.sites.active} inactive · ${d.sites.total} total`
                : null
            }
            onClick={() => void navigate('/sites')}
          />
        )}
        {d?.clients !== null && (
          <Stat
            label="Clients"
            icon={Store}
            value={d ? formatNumber(d.clients?.active) : <Skeleton width={60} height={28} />}
            foot={d?.clients ? `${d.clients.total} under contract` : null}
            onClick={() => void navigate('/clients')}
          />
        )}
        {d?.users !== null && (
          <Stat
            label="People with access"
            icon={Users}
            value={
              d ? (
                formatNumber(d.users ? d.users.total - d.users.disabled : 0)
              ) : (
                <Skeleton width={60} height={28} />
              )
            }
            foot={
              d?.users
                ? `${d.users.office} office · ${d.users.floor} floor · ${d.users.client} client`
                : null
            }
            onClick={() => void navigate('/users')}
          />
        )}
        {d?.devices !== null && (
          <Stat
            label="Scanners online-ready"
            icon={MonitorSmartphone}
            value={d ? formatNumber(d.devices?.active) : <Skeleton width={60} height={28} />}
            foot={
              d?.devices
                ? `${d.devices.pending} awaiting enrolment · ${d.devices.revoked} revoked`
                : null
            }
            onClick={() => void navigate('/devices')}
          />
        )}
      </div>

      {o ? (
        <div className="grid-stats">
          <Stat
            label="Units on hand"
            icon={Boxes}
            value={formatNumber(o.onHand)}
            foot={`${formatNumber(o.skusInStock)} SKUs in stock`}
            onClick={() => void navigate('/inventory')}
          />
          <Stat
            label="Available to promise"
            icon={PackageCheck}
            tone="ok"
            value={formatNumber(o.available)}
            foot={`${formatNumber(o.held)} on hold · ${formatNumber(o.allocated)} allocated`}
            onClick={() => void navigate('/inventory')}
          />
          <Stat
            label="Expiring in 30 days"
            icon={AlertTriangle}
            tone={o.expired ? 'danger' : 'warn'}
            value={formatNumber(o.expiring30)}
            foot={`${formatNumber(o.expired)} units expired`}
            onClick={() => void navigate('/inventory')}
          />
          <Stat
            label="Location accuracy (30 d)"
            icon={ClipboardCheck}
            tone="ok"
            value={o.accuracy30d === null ? '—' : `${o.accuracy30d}%`}
            foot={`${o.openCounts} counts open · target ≥ 99.5%`}
            onClick={() => void navigate('/inventory/counts')}
          />
        </div>
      ) : null}

      {d?.catalog || d?.space ? (
        <div className="grid-stats">
          {d.catalog ? (
            <Stat
              label="Items in catalog"
              icon={Package}
              value={formatNumber(d.catalog.activeItems)}
              foot={`${formatNumber(d.catalog.items - d.catalog.activeItems)} inactive`}
              onClick={() => void navigate('/items')}
            />
          ) : null}
          {d.space ? (
            <Stat
              label="Storage locations"
              icon={MapPin}
              value={formatNumber(d.space.locations)}
              foot={`${formatNumber(d.space.pickable)} pick faces · ${d.space.zones} zones`}
              onClick={() => void navigate('/locations')}
            />
          ) : null}
          {d.catalog && d.space ? (
            <Stat
              label="License plates"
              icon={Tag}
              value={formatNumber(d.catalog.lpns)}
              foot="Pallets, cases and totes on record"
              onClick={() => void navigate('/locations')}
            />
          ) : null}
        </div>
      ) : null}

      {d?.activity || d?.security ? (
        <div className="grid-2">
          {d.activity ? (
            <Card
              title="Activity, last 14 days"
              sub="Sign-ins and configuration changes recorded in the audit trail"
              actions={
                <div className="legend">
                  <span style={{ ['--c' as string]: 'var(--chart-1)' }}>Sign-ins</span>
                  <span style={{ ['--c' as string]: 'var(--chart-3)' }}>Changes</span>
                </div>
              }
            >
              <BarChart
                data={d.activity.map((a) => ({
                  label: new Date(`${a.date}T00:00:00Z`).toLocaleDateString(undefined, {
                    month: 'short',
                    day: 'numeric',
                    timeZone: 'UTC',
                  }),
                  signIns: a.signIns,
                  changes: a.changes,
                }))}
                series={[
                  { key: 'signIns', label: 'Sign-ins', color: 'var(--chart-1)' },
                  { key: 'changes', label: 'Changes', color: 'var(--chart-3)' },
                ]}
              />
            </Card>
          ) : null}
          {d.security && d.users ? <SecurityCard d={d} /> : null}
        </div>
      ) : null}

      <div className="grid-2">
        {d?.recent ? (
          <Card
            title="Recent activity"
            sub="Latest changes and sign-ins"
            actions={
              <Button size="sm" variant="ghost" onClick={() => void navigate('/audit')}>
                View all
              </Button>
            }
          >
            <AuditTimeline entries={d.recent} showDiff={false} />
          </Card>
        ) : (
          <div />
        )}
        {d ? <SetupChecklist d={d} /> : null}
      </div>

      {d?.users && d.users.total > 0 ? (
        <Card title="Who has access" sub="Active accounts by type">
          <ShareBars
            items={[
              {
                label: 'Office staff (web + two-step sign-in)',
                value: d.users.office,
                color: 'var(--chart-1)',
              },
              {
                label: 'Floor staff (scanner, badge + PIN)',
                value: d.users.floor,
                color: 'var(--chart-4)',
              },
              { label: 'Client portal users', value: d.users.client, color: 'var(--chart-3)' },
            ]}
          />
        </Card>
      ) : null}
    </div>
  );
}

function SecurityCard({ d }: { d: DashboardSummary }) {
  const u = d.users!;
  const s = d.security!;
  const webUsers = u.office + u.client - u.disabled;
  const enrolled = Math.max(0, webUsers - u.mfaPending);
  const adoption = pct(enrolled, webUsers);
  const alarms = s.tokenReuse7d > 0 || u.locked > 0;
  return (
    <Card
      title="Security posture"
      sub="Two-step sign-in coverage and sign-in health"
      actions={
        alarms ? (
          <Badge tone="danger" dot>
            Attention
          </Badge>
        ) : (
          <Badge tone="ok" dot>
            Healthy
          </Badge>
        )
      }
    >
      <div className="stack">
        <div className="stack-sm">
          <div className="row" style={{ justifyContent: 'space-between' }}>
            <span>Two-step sign-in enrolled</span>
            <strong className="num">{adoption}%</strong>
          </div>
          <div className={`meter ${adoption === 100 ? 'ok' : 'warn'}`}>
            <span style={{ width: `${adoption}%` }} />
          </div>
          <span className="small muted">
            {u.mfaPending} {u.mfaPending === 1 ? 'person sets' : 'people set'} it up at their next
            sign-in
          </span>
        </div>
        <div className="list" style={{ margin: '0 -20px -16px' }}>
          <SecurityRow icon={ShieldCheck} label="Sign-ins, last 24 hours" value={s.signIns24h} />
          <SecurityRow
            icon={ShieldAlert}
            label="Failed attempts, last 24 hours"
            value={s.failedAttempts24h}
            tone={s.failedAttempts24h > 10 ? 'warn' : undefined}
          />
          <SecurityRow
            icon={ShieldAlert}
            label="Locked accounts"
            value={u.locked}
            tone={u.locked ? 'danger' : undefined}
          />
          <SecurityRow
            icon={ShieldAlert}
            label="Stolen-token alarms, 7 days"
            value={s.tokenReuse7d}
            tone={s.tokenReuse7d ? 'danger' : undefined}
          />
        </div>
      </div>
    </Card>
  );
}

function SecurityRow({
  icon: Icon,
  label,
  value,
  tone,
}: {
  icon: typeof ShieldAlert;
  label: string;
  value: number;
  tone?: 'warn' | 'danger';
}) {
  return (
    <div className="list-item">
      <span className={`icon-tile ${tone ?? ''}`} style={{ width: 30, height: 30 }}>
        <Icon size={15} />
      </span>
      <span className="grow">{label}</span>
      <strong className={`num ${tone ? `${tone}-text` : ''}`}>{value}</strong>
    </div>
  );
}

function SetupChecklist({ d }: { d: DashboardSummary }) {
  const navigate = useNavigate();
  const steps = [
    d.sites && { done: d.sites.total > 0, label: 'Add your warehouse sites', to: '/sites' },
    d.clients && {
      done: d.clients.total > 0,
      label: 'Add the brands you store for',
      to: '/clients',
    },
    d.users && { done: d.users.office > 1, label: 'Invite office staff', to: '/users' },
    d.users && { done: d.users.floor > 0, label: 'Add floor staff with badges', to: '/users' },
    d.devices && { done: d.devices.active > 0, label: 'Enrol a handheld scanner', to: '/devices' },
    d.users && {
      done: d.users.mfaPending === 0,
      label: 'Everyone on two-step sign-in',
      to: '/users',
    },
  ].filter(Boolean) as { done: boolean; label: string; to: string }[];
  if (!steps.length) return <div />;
  const done = steps.filter((s) => s.done).length;
  return (
    <div data-tour="checklist" style={{ alignSelf: 'start' }}>
      <Card title="Go-live checklist" sub={`${done} of ${steps.length} complete`} flush>
        <div style={{ padding: '12px 20px 4px' }}>
          <div className={`meter ${done === steps.length ? 'ok' : ''}`}>
            <span style={{ width: `${pct(done, steps.length)}%` }} />
          </div>
        </div>
        <div className="list">
          {steps.map((s) => (
            <button
              key={s.label}
              type="button"
              className="list-item btn-ghost"
              style={{
                border: 0,
                background: 'none',
                cursor: 'pointer',
                textAlign: 'left',
                width: '100%',
              }}
              onClick={() => void navigate(s.to)}
            >
              {s.done ? (
                <CheckCircle2 size={18} className="ok-text" />
              ) : (
                <Circle size={18} className="faint" />
              )}
              <span className="grow" style={s.done ? { color: 'var(--muted)' } : undefined}>
                {s.label}
              </span>
            </button>
          ))}
        </div>
      </Card>
    </div>
  );
}
